import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Teacher } from '../types';
import { buildWhatsAppUrl } from '../utils/phoneUtils';
import { getGoogleDriveDirectLink } from '../utils/uploadUtils';

interface TeacherCardProps {
    teacher: Teacher;
}

export default function TeacherCard({ teacher }: TeacherCardProps) {
    const [imgError, setImgError] = useState(false);

    // Dynamic gradient background for names without images
    const getInitialsGradient = (name: string) => {
        const charCodeSum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const gradients = [
            'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            'linear-gradient(135deg, #f43f5e 0%, #fb923c 100%)',
            'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
        ];
        return gradients[charCodeSum % gradients.length];
    };

    const hasImage = teacher.imageUrl && !imgError;

    return (
        <div className="bg-white/96 rounded-3xl border border-slate-100 p-4 sm:p-6 shadow-sm hover:shadow-[0_24px_60px_rgba(15,23,42,0.08)] transition-all duration-300">
            <div className="flex flex-col md:flex-row gap-6">
                {/* Image Container */}
                <div className="w-full max-w-[8rem] h-32 md:w-40 md:h-40 mx-auto md:mx-0 shrink-0 rounded-2xl overflow-hidden bg-slate-100 relative">
                    {hasImage ? (
                        <img
                            alt={teacher.name}
                            className="w-full h-full object-cover"
                            src={getGoogleDriveDirectLink(teacher.imageUrl!)}
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        <div
                            className="w-full h-full flex items-center justify-center text-white text-4xl font-extrabold"
                            style={{ background: getInitialsGradient(teacher.name) }}
                        >
                            {teacher.name.charAt(0)}
                        </div>
                    )}
                    <div className="absolute top-2 right-2 bg-emerald-500 text-white p-1 rounded-full border-2 border-white shadow-sm">
                        <span className="material-symbols-outlined text-[12px] block">verified</span>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h3 className="text-xl font-extrabold text-navy mb-1 break-words">{teacher.name}</h3>
                            <p className="text-primary font-bold text-sm mb-3">
                                {teacher.subject} Specialist in {teacher.district || 'Online'}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-4">
                                <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-indigo-100">
                                    {teacher.subject}
                                </span>
                                {teacher.medium && (
                                    <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-emerald-100">
                                        {teacher.medium} Medium
                                    </span>
                                )}
                                {teacher.district && (
                                    <span className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-amber-100">
                                        {teacher.district}
                                    </span>
                                )}
                            </div>

                            <p className="text-slate-500 text-sm line-clamp-3 sm:line-clamp-2 leading-relaxed max-w-2xl break-words">
                                {teacher.bio || `Connect with ${teacher.name} for expert guidance in ${teacher.subject}. Available for ${teacher.specificLocation ? teacher.specificLocation + ' and ' : ''} online sessions.`}
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex flex-col gap-3">
                        <div className="sm:hidden flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600">
                            <span className="material-symbols-outlined text-primary text-base">location_on</span>
                            <span className="min-w-0 break-words">{teacher.specificLocation || teacher.district || 'Online'}</span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="hidden sm:flex flex-col text-xs font-bold text-slate-400 mr-2 min-w-max">
                            <span className="uppercase tracking-wider">Location</span>
                            <span className="text-navy text-sm font-extrabold flex items-center gap-1">
                                <span className="material-symbols-outlined text-primary text-base">location_on</span>
                                {teacher.specificLocation || teacher.district}
                            </span>
                        </div>
                        <Link
                            to={`/teacher/${teacher.id}`}
                            className="w-full sm:w-auto flex-1 bg-primary text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-sm shadow-lg shadow-primary/20"
                        >
                            View Profile →
                        </Link>
                        {teacher.whatsapp && (
                            <a
                                href={buildWhatsAppUrl(teacher.whatsapp)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto flex-1 bg-indigo-50 text-indigo-700 py-3 rounded-xl font-bold hover:bg-indigo-100 transition-all flex items-center justify-center gap-2 text-sm"
                            >
                                <span className="material-symbols-outlined text-sm">chat</span>
                                WhatsApp
                            </a>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
